import moment, { Moment } from 'moment';
import dayjs, { Dayjs, QUnitType } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';

dayjs.extend(quarterOfYear);

export type DatePresetKey =
  | 'today'
  | 'yesterday'
  | 'thisWeek'
  | 'lastWeek'
  | 'thisMonth'
  | 'lastMonth'
  | 'thisQuarter'
  | 'lastQuarter'
  | 'thisYear'
  | 'lastYear';

export const DATE_PRESET_LABELS: Record<DatePresetKey, string> = {
  today: 'Hôm nay',
  yesterday: 'Hôm qua',
  thisWeek: 'Tuần này',
  lastWeek: 'Tuần trước',
  thisMonth: 'Tháng này',
  lastMonth: 'Tháng trước',
  thisQuarter: 'Quý này',
  lastQuarter: 'Quý trước',
  thisYear: 'Năm nay',
  lastYear: 'Năm trước',
};

/** Thứ tự hiển thị mặc định: từ mốc ngắn nhất tới dài nhất. */
export const ALL_DATE_PRESETS: DatePresetKey[] = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
  'lastYear',
];

/** Dùng cho picker theo tháng/quý/năm — các mốc theo ngày không có ý nghĩa ở đó. */
export const PERIOD_DATE_PRESETS: DatePresetKey[] = [
  'thisMonth',
  'lastMonth',
  'thisQuarter',
  'lastQuarter',
  'thisYear',
  'lastYear',
];

/** moment và dayjs cùng API startOf/endOf/subtract nên chung một công thức. */
type DateUnit = 'day' | 'week' | 'month' | 'quarter' | 'year';

interface RangeRecipe {
  unit: DateUnit;
  /** Số đơn vị lùi về trước: 0 = kỳ hiện tại, 1 = kỳ trước. */
  offset: number;
}

const RECIPES: Record<DatePresetKey, RangeRecipe> = {
  today: { unit: 'day', offset: 0 },
  yesterday: { unit: 'day', offset: 1 },
  thisWeek: { unit: 'week', offset: 0 },
  lastWeek: { unit: 'week', offset: 1 },
  thisMonth: { unit: 'month', offset: 0 },
  lastMonth: { unit: 'month', offset: 1 },
  thisQuarter: { unit: 'quarter', offset: 0 },
  lastQuarter: { unit: 'quarter', offset: 1 },
  thisYear: { unit: 'year', offset: 0 },
  lastYear: { unit: 'year', offset: 1 },
};

const momentRange = ({ unit, offset }: RangeRecipe): [Moment, Moment] => {
  const base = offset === 0 ? moment() : moment().subtract(offset, unit);
  return [base.clone().startOf(unit), base.clone().endOf(unit)];
};

// dayjs tách kiểu 'quarter' (QUnitType) khỏi các đơn vị còn lại nên phải gọi riêng.
const dayjsSubtract = (offset: number, unit: DateUnit) =>
  unit === 'quarter' ? dayjs().subtract(offset, 'quarter' as QUnitType) : dayjs().subtract(offset, unit);

const dayjsRange = ({ unit, offset }: RangeRecipe): [Dayjs, Dayjs] => {
  const base = offset === 0 ? dayjs() : dayjsSubtract(offset, unit);
  return [base.startOf(unit), base.endOf(unit)];
};

/** Preset cho RangePicker dùng moment (antd DatePicker mặc định). */
export const momentPresetRanges = (
  keys: readonly DatePresetKey[] = ALL_DATE_PRESETS,
): Record<string, [Moment, Moment]> =>
  keys.reduce(
    (ranges, key) => ({ ...ranges, [DATE_PRESET_LABELS[key]]: momentRange(RECIPES[key]) }),
    {} as Record<string, [Moment, Moment]>,
  );

/** Preset cho RangePicker dùng dayjs (DayjsDatePicker). */
export const dayjsPresetRanges = (
  keys: readonly DatePresetKey[] = ALL_DATE_PRESETS,
): Record<string, [Dayjs, Dayjs]> =>
  keys.reduce(
    (ranges, key) => ({ ...ranges, [DATE_PRESET_LABELS[key]]: dayjsRange(RECIPES[key]) }),
    {} as Record<string, [Dayjs, Dayjs]>,
  );
